import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { DecorationSet } from '@atlaskit/editor-prosemirror/view';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIDefinitionsPlugin } from '../../aiDefinitionsPluginType';
import type { AIDefinitionsPluginConfig } from '../../types';
import { debouncedUpdateKeywordsAndDecorations } from './commands';
import { createPluginState, getPluginState } from './plugin-factory';
import { aiDefinitionsPluginKey } from './plugin-key';
import { getInitialState } from './state';
import type { AIDefinitionsPluginState } from './types';
import { onClickKeywordDecoration } from './util/keyword';
import { onMouseoutKeywordStyler, onMouseoverKeywordStyler } from './util/keyword-styler';

export const createAIDefinitionsPMPlugin = (
	{ dispatch }: PMPluginFactoryParams,
	{
		api,
		config,
	}: {
		// api is mandatory but can be undefined
		api: ExtractInjectionAPI<AIDefinitionsPlugin> | undefined;
		config: AIDefinitionsPluginConfig;
	},
): SafePlugin<AIDefinitionsPluginState> => {
	return new SafePlugin({
		key: aiDefinitionsPluginKey,

		state: createPluginState(dispatch, () => getInitialState({ config })),

		view: (editorView: EditorView) => {
			return {
				update: (editorView: EditorView, prevState: EditorState) => {
					const pluginState = getPluginState(editorView.state);
					const { enabled, autoHighlightEnabled } = pluginState;
					if (!enabled || !autoHighlightEnabled) {
						return;
					}

					const { keywordRegex, keywordDecorationsSessionCounter } = getPluginState(
						editorView.state,
					);
					const prevPluginState = getPluginState(prevState);

					// if it's view mode, and there is no keywordRegex, and it's a new session, update keywords and decorations
					if (
						api?.editorViewMode.sharedState.currentState()?.mode === 'view' &&
						!keywordRegex &&
						keywordDecorationsSessionCounter !== prevPluginState.keywordDecorationsSessionCounter
					) {
						debouncedUpdateKeywordsAndDecorations({ api, config, editorView });
					}
				},
			};
		},

		props: {
			decorations(state: EditorState) {
				const { enabled, autoHighlightEnabled, keywordDecorations } = getPluginState(state);

				if (!enabled || !autoHighlightEnabled) {
					return;
				}

				// currently, only show auto-highlights in view mode
				if (api?.editorViewMode.sharedState.currentState()?.mode === 'view') {
					return keywordDecorations;
				}

				return DecorationSet.empty;
			},

			// currently, only show auto-highlights in view mode
			// so we only need to handle DOM events in view mode
			// reading mode outside of callbacks is not accurate, so need to read it in callbacks
			handleDOMEvents: {
				mouseover: (editorView: EditorView, event: MouseEvent) => {
					const pluginState = getPluginState(editorView.state);
					const { enabled, autoHighlightEnabled } = pluginState;
					if (!enabled || !autoHighlightEnabled) {
						return;
					}

					if (api?.editorViewMode.sharedState.currentState()?.mode === 'view') {
						onMouseoverKeywordStyler(event);
					}
				},

				mouseout: (editorView: EditorView, event: MouseEvent) => {
					const pluginState = getPluginState(editorView.state);
					const { enabled, autoHighlightEnabled } = pluginState;
					if (!enabled || !autoHighlightEnabled) {
						return;
					}

					if (api?.editorViewMode.sharedState.currentState()?.mode === 'view') {
						onMouseoutKeywordStyler(event);
					}
				},

				click: (editorView: EditorView, event: MouseEvent) => {
					const pluginState = getPluginState(editorView.state);
					const { enabled, autoHighlightEnabled } = pluginState;
					if (!enabled || !autoHighlightEnabled) {
						return;
					}

					if (api?.editorViewMode.sharedState.currentState()?.mode === 'view') {
						onClickKeywordDecoration({ editorView, event, api });
					}
				},
			},
		},
	});
};
